// User-uploaded files are no longer echoed back as dedicated `input_image` /
// `input_document` events. As of Rain 0.178.0 a file sent with a message is
// staged into the thread's `uploads://` artifact store and surfaced in the
// event stream as a `reminder` event immediately preceding the user's
// `input_text` — the agent reads the bytes on demand via the `artifact_read`
// tool. This parses that reminder into the attachment metadata the user bubble
// renders as a chip (mirrors the Rain designer UI, `UPLOAD_REMINDER_RE` in
// `ui/src/modules/conversation/api.ts`). See ENG-4666 / ENG-4592.

/**
 * Attachment metadata recovered from an upload reminder.
 * `kind` is derived from the reported MIME type; `filename` and `uri` come
 * straight from the reminder's leading sentence.
 */
export interface IUploadReminderAttachment {
  kind: 'image' | 'document';
  filename: string;
  uri: string;
}

// Only the leading sentence is matched — "The user uploaded 'NAME' (MIME, N
// bytes[, N lines]) to URI." — so the trailing model-facing guidance can
// evolve independently. This format is a stable contract with the backend
// reminder emitter; keep it in sync with Rain's `AgentPool::attachment_reminder`.
// The URI is `uploads://<raw filename>`, so it can contain spaces and
// parentheses ("uploads://PaymentReceipt (30).pdf") — it's terminated by the
// sentence-ending period, not by whitespace.
const UPLOAD_REMINDER_RE =
  /^The user uploaded '(.+)' \((.+?), \d+ bytes(?:, \d+ lines)?\) to (.+?)\.(?=\s|$)/;

/**
 * Parses an upload reminder's text into attachment metadata, or returns `null`
 * for any other reminder (plan nudges, completion summaries, etc.) so those
 * stay hidden from the UI as LLM-only context.
 */
export function parseUploadReminder(
  text: string | undefined,
): IUploadReminderAttachment | null {
  if (!text) return null;
  const match = UPLOAD_REMINDER_RE.exec(text);
  if (!match) return null;
  const [, filename, mime, uri] = match;
  return {
    kind: mime.startsWith('image/') ? 'image' : 'document',
    filename,
    uri,
  };
}
